/**
 * Central, pure entity-label formatting (request 0077, spec 0028 Fase 1).
 *
 * `formatEntityLabel` is the SSOT for "how a record appears" — consumed by
 * `EntityLookupField` (console), and meant to be reused later by FK grid
 * columns and printing (see spec 0028). The org chooses *which* named format
 * to use (via the `display` org-param group); this module owns what each
 * named format means and how it degrades when data is missing.
 */
/** Registry of display-format specs per entity name. */
export const ENTITY_DISPLAY_FORMATS = {
    Product: {
        available: [
            'description',
            'code+description',
            'reference',
            'code+reference',
            'reference+description',
        ],
        default: 'description',
        numericSearchFields: ['code'],
    },
    /** Lookup label shows code + description (0160). No org-param picker yet. */
    StandardPackaging: {
        available: ['code+description', 'description'],
        default: 'code+description',
        numericSearchFields: ['code'],
    },
    /** Lookup shows code + description (0198). `code` is numeric in the descriptor. */
    StockLocation: {
        available: ['code+description', 'description'],
        default: 'code+description',
        numericSearchFields: ['code'],
    },
};
/** Fields (in join order) each named format reads from the record. */
const FORMAT_FIELDS = {
    description: ['description'],
    'code+description': ['code', 'description'],
    reference: ['reference'],
    'code+reference': ['code', 'reference'],
    'reference+description': ['reference', 'description'],
};
/**
 * Fields that compose a named display format — also the SSOT for which columns
 * a server-side lookup should search when that format is active.
 */
export function getDisplayFormatFields(format) {
    return FORMAT_FIELDS[format] ?? FORMAT_FIELDS.description;
}
function fieldPart(record, field) {
    const raw = record[field];
    if (raw == null || raw === '')
        return undefined;
    return String(raw);
}
function buildLabel(record, format) {
    const fields = getDisplayFormatFields(format);
    const parts = fields.map((f) => fieldPart(record, f)).filter((p) => !!p);
    return parts.join(' - ');
}
/**
 * Builds the display label for a record. **Pure, no I/O.**
 *
 * Fallback chain:
 * - format absent/invalid for the entity → degrades to `'description'`;
 * - a field the format needs is absent/empty on the record → that part is
 *   omitted (never a dangling separator);
 * - the chosen format resolves to nothing → retries `'description'`;
 * - still nothing → `String(record.id)` (preserves the pre-0077 `getLabel` behavior).
 */
export function formatEntityLabel(entityName, record, format) {
    const spec = ENTITY_DISPLAY_FORMATS[entityName];
    const isValidFormat = !!format && (!spec || spec.available.includes(format));
    const resolvedFormat = isValidFormat ? format : 'description';
    const label = buildLabel(record, resolvedFormat);
    if (label)
        return label;
    if (resolvedFormat !== 'description') {
        const fallback = buildLabel(record, 'description');
        if (fallback)
            return fallback;
    }
    return String(record.id ?? '');
}
/**
 * Composes a variant's display label from an already-formatted product label
 * and the labels of its variation values (color, options). **Pure.**
 *
 * `productLabel` must already be formatted by the caller (the application
 * layer, which knows the org's active `EntityDisplayFormat`) — this function
 * only decides the join order and drops empty value labels.
 */
export function composeVariantLabel(productLabel, valueLabels, format) {
    const values = valueLabels.filter((l) => !!l);
    const resolvedFormat = format === 'values-product' ? 'values-product' : 'product-values';
    const parts = resolvedFormat === 'values-product' ? [...values, productLabel] : [productLabel, ...values];
    return parts.filter((p) => !!p).join(' - ');
}
//# sourceMappingURL=entityLabelFormat.js.map